import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Box,
  Pagination,
  Link
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DownloadIcon from '@mui/icons-material/Download';
import { format } from 'date-fns';
import AudioPlayer from './AudioPlayer';
import { getRecordingUrl } from '../services/api';
import { getConfig } from '../services/config';

function RecordingsTable({ recordings, currentPage, totalPages, onPageChange }) {
  const [playingRecording, setPlayingRecording] = useState(null);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    getConfig().then(setConfig).catch(console.error);
  }, []);

  // Generate ConnectWise ticket URL
  const getTicketUrl = (ticketNumber) => {
    if (!config?.connectwiseUrl || !ticketNumber) return null;
    return `${config.connectwiseUrl}?routeTo=ServiceFXSR/${ticketNumber}`;
  };

  const getDirectionColor = (direction) => {
    switch (direction) {
      case 'Inbound': return 'primary';
      case 'Outbound': return 'secondary';
      case 'Internal': return 'default';
      default: return 'default';
    }
  };

  const formatCallDateTime = (dateTimeStr) => {
    if (!dateTimeStr) {
      return 'Date Unknown';
    }
    try {
      return format(new Date(dateTimeStr), 'MMM dd, yyyy HH:mm:ss');
    } catch (error) {
      console.error('Invalid date:', dateTimeStr, error);
      return 'Invalid Date';
    }
  };

  const handlePlay = (recording) => {
    setPlayingRecording(recording);
  };

  const handleDownload = async (recording) => {
    try {
      const data = await getRecordingUrl(recording.blobPath, 'download');
      window.open(data.url, '_blank');
    } catch (err) {
      console.error('Download failed:', err);
      alert('Failed to download recording. Please try again.');
    }
  };

  const handleClosePlayer = () => {
    setPlayingRecording(null);
  };

  if (recordings.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        No recordings found. Try adjusting your search filters.
      </Paper>
    );
  }

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date/Time</TableCell>
              <TableCell>Extension</TableCell>
              <TableCell>Direction</TableCell>
              <TableCell>Phone Number</TableCell>
              {config?.ticketSearchEnabled && <TableCell>Ticket</TableCell>}
              <TableCell>Type</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {recordings.map((recording) => (
              <TableRow key={recording.id} hover>
                <TableCell>
                  {formatCallDateTime(recording.callDateTime)}
                </TableCell>
                <TableCell>
                  <strong>{recording.extensionName || recording.extension}</strong>
                  <br />
                  <small>({recording.extension})</small>
                </TableCell>
                <TableCell>
                  <Chip
                    label={recording.direction}
                    size="small"
                    color={getDirectionColor(recording.direction)}
                  />
                </TableCell>
                <TableCell>
                  {recording.otherPartyNumber || ' -'}
                </TableCell>
                {config?.ticketSearchEnabled && (
                  <TableCell>
                    {recording.tickets && recording.tickets.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {recording.tickets.map((ticket, idx) => {
                          const ticketNum = ticket.ticket_number || ticket.ticketNumber;
                          const ticketUrl = getTicketUrl(ticketNum);
                          return ticketUrl ? (
                            <Link
                              key={idx}
                              href={ticketUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ textDecoration: 'none' }}
                            >
                              <Chip
                                label={`#${ticketNum}`}
                                size="small"
                                color="success"
                                title={ticket.ticket_summary || ticket.ticketSummary || 'Click to open in ConnectWise'}
                                clickable
                              />
                            </Link>
                          ) : (
                            <Chip
                              key={idx}
                              label={`#${ticketNum}`}
                              size="small"
                              color="success"
                              title={ticket.ticket_summary || ticket.ticketSummary || ''}
                            />
                          );
                        })}
                      </Box>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                )}
                <TableCell>
                  {recording.callType === 'Voicemail' ? (
                    <Chip label="Voicemail" size="small" color="warning" />
                  ) : (
                    'Call'
                  )}
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => handlePlay(recording)}
                    color="primary"
                    title="Play recording"
                  >
                    <PlayArrowIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDownload(recording)}
                    color="primary"
                    title="Download recording"
                  >
                    <DownloadIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(e, page) => onPageChange(page)}
            color="primary"
          />
        </Box>
      )}

      {playingRecording && (
        <AudioPlayer
          recording={playingRecording}
          onClose={handleClosePlayer}
        />
      )}
    </>
  );
}

export default RecordingsTable;
